import { apiDate, boolean, optionalString, string, uuid, zenum, zodLess, ZodLessInfer } from '@kaiizen/zod-less';

import { FeatureFlagName } from './FeatureFlagRegistry';

/**
 * Each supplier other than THD rolls out behind its own catalog flag, keyed by warehouse supplier slug.
 * supplierCatalogV2 is not one of them: it switches existing THD catalog reads to the new tables.
 */
export const SUPPLIER_CATALOG_FEATURE_FLAGS = {
  lowes: 'supplierLowesCatalog',
  wayfair: 'supplierWayfairCatalog',
  hd_supply: 'supplierHdSupplyCatalog',
  daikin_motili: 'supplierDaikinMotiliCatalog',
} as const satisfies Record<string, FeatureFlagName>;

/** The flag a supplier needs on, or undefined when it needs none (THD). */
export const getSupplierCatalogFeatureFlag = (slug: string): FeatureFlagName | undefined =>
  (SUPPLIER_CATALOG_FEATURE_FLAGS as Record<string, FeatureFlagName>)[slug];

export const ApiSupplierAccessScopeSchema = zenum('default', 'org');
export type ApiSupplierAccessScope = ZodLessInfer<typeof ApiSupplierAccessScopeSchema>;

export const ApiWarehouseSupplierSchema = zodLess({
  id: uuid,
  slug: string,
  name: string,
  hasCatalog: boolean,
  active: boolean,
  ownerOrgId: optionalString,
});
export type ApiWarehouseSupplier = ZodLessInfer<typeof ApiWarehouseSupplierSchema>;

export const ApiOrgSupplierSchema = zodLess({
  id: uuid,
  slug: string,
  name: string,
  hasCatalog: boolean,
});
export type ApiOrgSupplier = ZodLessInfer<typeof ApiOrgSupplierSchema>;

export const ApiSupplierAccessRecordSchema = zodLess({
  id: uuid,
  supplierId: uuid,
  scope: ApiSupplierAccessScopeSchema,
  orgId: optionalString,
  enabled: boolean,
  createdAt: apiDate,
  updatedAt: apiDate,
});
export type ApiSupplierAccessRecord = ZodLessInfer<typeof ApiSupplierAccessRecordSchema>;

export const ApiSetSupplierAccessSchema = zodLess({
  supplierId: uuid,
  scope: ApiSupplierAccessScopeSchema,
  orgId: optionalString,
  enabled: boolean,
});
export type ApiSetSupplierAccess = ZodLessInfer<typeof ApiSetSupplierAccessSchema>;

export const ApiSupplierAccessOverviewSchema = zodLess({
  suppliers: [ApiWarehouseSupplierSchema],
  records: [ApiSupplierAccessRecordSchema],
});
export type ApiSupplierAccessOverview = ZodLessInfer<typeof ApiSupplierAccessOverviewSchema>;

export const getSupplierAccessOverviewQueryKey = () => 'supplier-access-all';
